import { CliVerificationStatus } from '../models/cli-verification.model';
import { isTestDeployment } from '../helpers/deploy-environment.helper';

/**
 * PS-6495 — mock harness for reviewing the guided setup on TEST. It was meant to go before the live
 * release (Torsten, 2026-09-11); the flow went live on 2026-10-28 with the harness kept, because
 * every one of these states costs a billed SIP-Account or a condition nobody can arrange on demand
 * to reach for real, and the review of follow-up changes still needs them.
 *
 * That is safe to carry because it is dead twice over on a live deployment, not once:
 * `mockScenario()` returns null unless `environment.URL_SUB_DOMAIN` contains 'test' AND the
 * `ps6495Mock` query parameter is present. The subdomain gate is the load-bearing one — it does
 * not depend on anyone remembering to leave a parameter off a URL.
 *
 * When it is removed, the full list, because the pieces are not all in one place:
 *
 * - this file and `mock-callnote.fixture.ts`
 * - `shared/interceptors/mock-backend.interceptor.ts` and its registration
 * - the `isMockMode()` branch in `callback-on-callnote.ts` (preselects the first suggestion)
 * - the `sipExtensionPrice` / `currency` overrides the interceptor applies per scenario
 * - the `/localization/v2/i18n` branch, which serves the brand's legal URLs so a mocked
 *   walkthrough still shows the consent links
 * - the `/phoneservice/v3/cli-verification/` branch (PS-6589) and its scripted poll state
 * - the PS-6586 `/bookingmgmt/v2/outbox/linkorders*` branches and the `link*` scenarios
 *
 * **Why this exists rather than "just click through it".** Several states of this flow cannot be
 * reached for real without cost or luck: ordering a SIP-Account books a **billed** number and
 * cannot be repeated, and a refused sign-in, a two-factor prompt, an ambiguous token or a rejected
 * booking depend on conditions nobody can arrange on demand. Reviewing the flow needs all of them.
 *
 * In mock mode **no request leaves the browser**: `mockBackendInterceptor` answers every endpoint
 * this flow touches, and the login is answered locally rather than reaching
 * a real authorization server.
 *
 * Gated exactly like the login trigger: on `URL_SUB_DOMAIN`, which the DEPLOY job injects, and not
 * on `environment.production`, which only says how the bundle was built. A live build reads
 * `connect.` and can never enter mock mode, whatever URL is used.
 */
export type MockScenario =
	/**
	 * Nothing configured: no extension, no SIP number, no outgoing number. The longest path —
	 * sign in, enter your own number, then book.
	 */
	| 'allMissing'
	/** The recorded state: no SIP-Account, outgoing number already configured. */
	| 'sipMissing'
	/** A SIP-Account exists, no number to signal. */
	| 'numberMissing'
	/**
	 * The recorded callnote's candidate list, restored.
	 *
	 * An EMPTY list is the default for every scenario, because that is the common case (Torsten,
	 * 2026-09-11) — a new customer has nothing to pick from and types the number. This scenario is
	 * the opposite case, kept reachable so the pick-from-a-list path does not go unreviewed.
	 *
	 * It is the inverse of what used to be `noCandidates`: back then the recording's list was the
	 * default and emptying it was the special case. Flipping which one needs asking for follows the
	 * traffic, not the fixture.
	 */
	| 'withCandidates'
	/** Nothing missing — the guided setup must not appear at all. */
	| 'complete'
	/** The proxy refuses the credentials. The customer stays on the sign-in step. */
	| 'loginRejected'
	/** The account needs a second factor, so the code field appears. Not a failure. */
	| 'loginTwoFactor'
	/** The token vouches for two phone services, so the flow must refuse to book. */
	| 'ambiguousToken'
	/**
	 * PS-6646: the sign-in resolves a phone service the callnote does not belong to, so the setup
	 * refuses it on the sign-in step with `sip_login_wrong_account`.
	 */
	| 'wrongAccount'
	/** The booking is refused outright — an HTTP error. */
	| 'orderFails'
	/**
	 * PS-6605: the booking is refused with 422 ADDRESS_INVALID - the contract address could not be
	 * used, nothing was booked. Shows the address screen.
	 */
	| 'orderAddressInvalid'
	/**
	 * The booking answers 200 but WITHOUT a number. That is a refusal too, and the more likely
	 * shape of one: the endpoint's `result` field is an undocumented bare string, so a missing
	 * number is the only signal we can read with confidence.
	 */
	| 'orderRejected'
	/**
	 * The booking answers `ALREADY_EXISTS` — the account was already there. A SUCCESS, and the case
	 * that would have been mistaken for a refusal by anything branching on `result === 'ORDERED'`.
	 */
	| 'orderAlreadyExists'
	/** PS-6526 price states. `null` price means NOT ON OFFER, which is not the same as "no figure". */
	| 'priceMissing'
	/** Price `0` — a real free offer, which must render rather than disappear. */
	| 'priceFree'
	/** Price present, currency missing: no figure may be shown, so the order is not offered (M3). */
	| 'priceNoCurrency'
	/** A non-EUR brand, because defaulting the currency would be a legally wrong figure. */
	| 'priceForeign'
	/** PS-6589: A SIP-Account exists and a number is configured, but it is not proven yet (setup case 7). */
	| 'numberUnverified'
	/** PS-6589: phoneservice rejects the number as a mobile in the number step. */
	| 'validateMobile'
	/** PS-6589: phoneservice answers UNAVAILABLE in the number step (held by another customer). */
	| 'validateUnavailable'
	/** PS-6589: The customer already proved the number: the verification call is skipped. */
	| 'validateAlreadyVerified'
	/** PS-6589: The number check fails technically; the run continues (fail-open). */
	| 'validateDown'
	/** PS-6589: The verification call is answered and the code is right. */
	| 'verifySuccess'
	/** PS-6589: The code was wrong too often within the call. */
	| 'verifyWrongCode'
	/** PS-6589: Nobody picks up. */
	| 'verifyNotAnswered'
	/** PS-6589: The line is busy. */
	| 'verifyBusy'
	/** PS-6589: An answering machine picks up. */
	| 'verifyVoicemail'
	/** PS-6589: The trigger is refused: the number is locked, with a retry time. */
	| 'verifyLocked'
	/** PS-6589: The call ends in a lock-out after wrong codes, with the lock time. */
	| 'verifyLockedOut'
	/** PS-6589: The trigger is refused by a rate limit, with a retry time. */
	| 'verifyRateLimited'
	/** PS-6589: Another tab already has a call running for this number. */
	| 'verifyInFlight'
	/** PS-6589: Our side could not place the call. */
	| 'verifyCallNotPlaced'
	/** PS-6589: The server closes the attempt as TIMED_OUT. */
	| 'verifyTimedOut'
	/** PS-6589: The server never reports an end; the page gives up after the deadline. */
	| 'verifyClientTimeout'
	/** PS-6589: Right after the booking the trunk is not on the contract yet (NOT_ELIGIBLE_NO_SIP_TRUNK). */
	| 'verifyNoSipYet'
	/** PS-6589: Cancel arrives after the bot finished: the cancel answers VERIFIED. */
	| 'verifyCancelRace'
	/** SIP order link (`?sipOrder&uid=<anything>&ps6495Mock=link…`): offer OPEN, order succeeds. */
	| 'linkOpen'
	/** The offer answers COMPLETED. */
	| 'linkCompleted'
	/** The offer answers EXPIRED. */
	| 'linkExpired'
	/** The offer answers INVALID - must look exactly like `linkExpired`. */
	| 'linkInvalid'
	/** The offer answers OPEN and the order 502, shown as `failed`. */
	| 'linkOrderFails'
	/** The offer answers ADDRESS_INCOMPLETE. */
	| 'linkNotOrderable';

const SCENARIOS: readonly MockScenario[] = [
	'allMissing',
	'sipMissing',
	'numberMissing',
	'withCandidates',
	'complete',
	'loginRejected',
	'loginTwoFactor',
	'ambiguousToken',
	'wrongAccount',
	'orderFails',
	'orderAddressInvalid',
	'orderRejected',
	'orderAlreadyExists',
	'priceMissing',
	'priceFree',
	'priceNoCurrency',
	'priceForeign',
	'numberUnverified',
	'validateMobile',
	'validateUnavailable',
	'validateAlreadyVerified',
	'validateDown',
	'verifySuccess',
	'verifyWrongCode',
	'verifyNotAnswered',
	'verifyBusy',
	'verifyVoicemail',
	'verifyLocked',
	'verifyLockedOut',
	'verifyRateLimited',
	'verifyInFlight',
	'verifyCallNotPlaced',
	'verifyTimedOut',
	'verifyClientTimeout',
	'verifyNoSipYet',
	'verifyCancelRace',
	'linkOpen',
	'linkCompleted',
	'linkExpired',
	'linkInvalid',
	'linkOrderFails',
	'linkNotOrderable',
];

const PARAM = 'ps6495Mock';

/** The scenario in force, or null when mock mode is off. Read once per page load. */
export function mockScenario(): MockScenario | null {
	// Never on a live deployment. See the class comment for why this is not `production`.
	if (!isTestDeployment()) {
		return null;
	}

	const raw = new URLSearchParams(location.search).get(PARAM);
	if (raw === null) {
		return null;
	}
	// `&ps6495Mock` with no value is the common case: default to the fullest path.
	if (raw === '') {
		return 'allMissing';
	}
	return SCENARIOS.includes(raw as MockScenario) ? (raw as MockScenario) : 'allMissing';
}

export function isMockMode(): boolean {
	return mockScenario() !== null;
}

const REAL_LOGIN_PARAM = 'ps6495RealLogin';

/**
 * TEMPORARY — mocks everything EXCEPT the login.
 *
 * With this on, `/rest/login` and `/authorization/**` are left alone: the credentials really go to
 * the login proxy, a real token comes back and `check_token` really resolves the customer's phone
 * service. Only the callnote, the booking and the task creation stay mocked, so a full rehearsal
 * costs no billed SIP number and creates no outbound task.
 *
 * It is a separate switch from the scenario because the two answer different questions: the
 * scenario decides which branch of the flow is walked, this decides whether the auth chain is the
 * real one. Without it the login is short-circuited, which is right for offline work on the UI and
 * wrong for proving the chain.
 */
export function isRealLoginMode(): boolean {
	return isMockMode() && new URLSearchParams(location.search).has(REAL_LOGIN_PARAM);
}

/** The three payload fields each scenario overrides on the recorded callnote. */
export function mockSetupSignals(scenario: MockScenario): {
	hasSipNumber: boolean;
	hasSipExtension: boolean;
	outboundOutgoingPhoneNumber: string | null;
	cliVerificationStatus?: CliVerificationStatus | null;
} {
	// The verification outcomes are reviewed on the short path (trunk held, number to enter), so
	// each screen is a few clicks away. The path through the order is `allMissing` and `verifyNoSipYet`.
	if ((scenario.startsWith('verify') && scenario !== 'verifyNoSipYet') || scenario.startsWith('validate')) {
		return { hasSipNumber: true, hasSipExtension: true, outboundOutgoingPhoneNumber: null };
	}
	switch (scenario) {
		case 'numberUnverified':
			return {
				hasSipNumber: true,
				hasSipExtension: true,
				outboundOutgoingPhoneNumber: '+4930123456',
				cliVerificationStatus: 'PENDING',
			};
		case 'numberMissing':
			// Extension AND SIP number booked; only the outgoing number is missing.
			return { hasSipNumber: true, hasSipExtension: true, outboundOutgoingPhoneNumber: null };
		case 'complete':
			// Everything in place, so the guided setup must not appear at all.
			return {
				hasSipNumber: true,
				hasSipExtension: true,
				outboundOutgoingPhoneNumber: '+4930123456',
				cliVerificationStatus: 'VERIFIED',
			};
		case 'sipMissing':
			return {
				hasSipNumber: false,
				hasSipExtension: false,
				outboundOutgoingPhoneNumber: '+497725252525',
			};
		default:
			// allMissing and every failure scenario walk the longest path to reach their step.
			return { hasSipNumber: false, hasSipExtension: false, outboundOutgoingPhoneNumber: null };
	}
}
